<template>
  <section class="flex flex-col gap-12 items-center justify-center overflow-hidden px-4 py-16 relative w-full bg-white md:px-16">
    <div class="mx-auto w-full max-w-[1200px] min-w-0">
      <div class="flex w-full flex-col items-center gap-12">
        <div class="flex w-full flex-col gap-1 text-center">
          <p
            v-if="overlineText"
            class="text-sm font-semibold uppercase leading-5 tracking-[1px]"
          >
            {{ overlineText }}
          </p>
          <h2 class="text-[32px] font-medium leading-10 tracking-[-0.02em] lg:text-[36px] lg:leading-[44px]">
            {{ headingText }}
          </h2>
        </div>

        <div class="flex w-full flex-col items-center gap-1 lg:gap-4">
          <SpecialisationFilterList
            v-model="selectedSpecialisationId"
            :specialisations="specialisations"
            :loading="loading"
            variant="page"
            :show-heading="false"
            section-class="w-full min-w-0"
            show-all-option
            scrollable-mobile
            compact
          />

          <p v-if="loading" class="w-full text-center text-base text-neutral-500">
            {{ t('services.loading') }}
          </p>

          <p
            v-else-if="filteredServices.length === 0"
            class="w-full text-center text-base text-neutral-500"
          >
            {{ t('services.empty_carousel') }}
          </p>

          <!--
            -mx-6 + w-[calc(100%+3rem)]: viewport rozszerzony 24px na każdą stronę dla cieni bocznych.
            pb-7 / pb-8: miejsce na dolny cień kart (8px offset + 24px blur − 3px spread ≈ 29px).
          -->
          <div
            v-else
            ref="viewportRef"
            class="services-carousel-viewport -mx-6 w-[calc(100%+3rem)] cursor-grab overflow-x-hidden overflow-y-visible touch-pan-y"
            :class="viewportClass"
            v-bind="swipeHandlers"
          >
            <div class="px-6 pt-1 pb-7 lg:pt-3 lg:pb-8">
              <div
                data-carousel-track
                class="flex"
                :class="[trackClass, trackTransitionClass]"
                :style="trackStyle"
              >
                <div
                  v-for="(page, pageIndex) in pages"
                  :key="pageIndex"
                  class="w-(--card-width) shrink-0 min-w-0"
                >
                  <div class="grid auto-rows-fr grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 lg:gap-5">
                    <Link
                      v-for="service in page"
                      :key="service.id"
                      :href="serviceHref(service)"
                      class="group flex h-full min-w-0 flex-col items-start gap-2 rounded-xl bg-white p-6 no-underline shadow-[0px_8px_24px_-3px_rgba(16,24,40,0.05),0px_8px_24px_-3px_rgba(16,24,40,0.1)] transition-shadow hover:shadow-md"
                    >
                      <span
                        class="inline-flex w-fit items-center rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-sm font-medium text-neutral-700"
                      >
                        {{ service.categoryLabel }}
                      </span>
                      <h3 class="line-clamp-2 min-h-[3.5rem] min-w-0 break-words text-xl font-semibold leading-7 tracking-[-0.02em] text-neutral-900">
                        {{ service.name }}
                      </h3>
                      <span
                        class="mt-1 inline-flex items-center gap-2 text-sm font-medium transition-colors group-hover:gap-3"
                        :class="accentTextClass"
                      >
                        {{ t('common.more') }}
                        <svg class="size-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <template v-if="!loading && pages.length > 1">
            <!-- Mobile -->
            <div class="flex lg:hidden h-11 w-full items-center justify-between">
              <CarouselDots
                :count="dotCount"
                :active-index="slideIndex"
                :dot-size="8"
                :gap="8"
              >
                <template #default="{ index, active }">
                  <button
                    type="button"
                    class="size-2 shrink-0 cursor-pointer rounded-full border-0 p-0 transition-all duration-300 ease-in-out"
                    :class="active ? 'opacity-100 scale-125' : 'opacity-30 scale-100'"
                    :style="{ backgroundColor: 'var(--color-text)' }"
                    :aria-label="t('carousel.goto_slide', { n: index + 1 })"
                    @click="goToSlide(index)"
                  />
                </template>
              </CarouselDots>

              <div class="flex items-center gap-2">
                <CarouselArrowButton
                  direction="prev"
                  :aria-label="t('carousel.services_prev')"
                  :disabled="!canGoPrev"
                  @click="prev"
                />
                <CarouselArrowButton
                  direction="next"
                  :aria-label="t('carousel.services_next')"
                  :disabled="!canGoNext"
                  @click="next"
                />
              </div>
            </div>

            <!-- Desktop -->
            <div class="relative hidden lg:block h-11 w-full">
              <CarouselDots
                class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                :count="dotCount"
                :active-index="slideIndex"
                :dot-size="6"
                :gap="8"
              >
                <template #default="{ active }">
                  <span
                    class="size-1.5 shrink-0 rounded-full transition-all duration-300 ease-in-out"
                    :class="active ? 'opacity-100 scale-125' : 'opacity-30 scale-100'"
                    :style="{ backgroundColor: 'var(--color-text)' }"
                  />
                </template>
              </CarouselDots>

              <CarouselArrowButton
                direction="next"
                class="absolute right-0 top-1/2 -translate-y-1/2"
                :aria-label="t('carousel.services_next')"
                :disabled="!canGoNext"
                @click="next"
              />
              <CarouselArrowButton
                direction="prev"
                class="absolute right-14 top-1/2 -translate-y-1/2"
                :aria-label="t('carousel.services_prev')"
                :disabled="!canGoPrev"
                @click="prev"
              />
            </div>
          </template>
        </div>

        <div
          v-if="!loading && filteredServices.length"
          class="flex w-full flex-col items-center justify-center gap-4 sm:flex-row sm:gap-6"
        >
          <Link
            v-if="examinationsButtonLabel"
            :href="examinationsHref"
            class="inline-flex h-12 w-full items-center justify-center gap-2 rounded-lg border px-5 py-[14px] text-base font-semibold no-underline transition-colors sm:w-auto"
            :class="[accentBorderClass, accentTextClass, accentHoverBgClass]"
          >
            {{ examinationsButtonLabel }}
            <svg class="size-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </Link>

          <Link
            v-if="proceduresButtonLabel"
            :href="proceduresHref"
            class="inline-flex h-12 w-full items-center justify-center gap-2 rounded-lg border px-5 py-[14px] text-base font-semibold no-underline transition-colors sm:w-auto"
            :class="[accentBorderClass, accentTextClass, accentHoverBgClass]"
          >
            {{ proceduresButtonLabel }}
            <svg class="size-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { Link } from '@inertiajs/vue3';
import SpecialisationFilterList from '@/Shared/SpecialisationFilterList.vue';
import CarouselArrowButton from '@/Shared/CarouselArrowButton.vue';
import CarouselDots from '@/Shared/CarouselDots.vue';
import { service as serviceRoute, services_index as servicesIndexRoute } from '@/routes.js';
import { useTranslations } from '@/utils/translations.js';
import { useCarouselSwipe } from '@/utils/useCarouselSwipe.js';
import { carouselTrackTransform } from '@/utils/carouselTrack.js';

const { t } = useTranslations();

const props = defineProps({
  overlineText:            { type: String, default: '' },
  headingText:             { type: String, default: 'Zabiegi i badania' },
  examinationsButtonLabel: { type: String, default: 'Zobacz wszystkie badania' },
  proceduresButtonLabel:   { type: String, default: 'Zobacz wszystkie zabiegi' },
  // eslint-disable-next-line vue/prop-name-casing
  site_context: { type: String, default: null },
  // eslint-disable-next-line vue/prop-name-casing
  preview_mode: { type: Boolean, default: false },
});

// Number of cards per carousel page (3 columns x 2 rows on desktop, per Figma).
const PER_PAGE = 6;
const MOBILE_BREAKPOINT = 1024;

const isMobileViewport = ref(false);
let mobileMediaQuery = null;
let syncMobileViewport = null;

const ACCENT_BY_SITE = {
  code_medical_center: {
    border: 'border-code-medical-center',
    text: 'text-code-medical-center',
    hover: 'hover:bg-code-medical-center-secondary',
  },
  infertility_treatment_clinic: {
    border: 'border-infertility-clinic',
    text: 'text-infertility-clinic',
    hover: 'hover:bg-infertility-clinic-secondary',
  },
  family_clinic: {
    border: 'border-family-clinic',
    text: 'text-family-clinic',
    hover: 'hover:bg-family-clinic-secondary',
  },
  institute: {
    border: 'border-institute',
    text: 'text-institute',
    hover: 'hover:bg-institute-secondary',
  },
};

const accent = computed(() => (
  ACCENT_BY_SITE[props.site_context] || ACCENT_BY_SITE.family_clinic
));
// Figma carousel controls use neutral text color in every context; only the
// hover fill keeps the per-clinic tint.
const accentBorderClass = 'border-[var(--color-text)]';
const accentTextClass = 'text-[var(--color-text)]';
const accentHoverBgClass = computed(() => accent.value.hover);

const services = ref([]);
const loading = ref(true);
const selectedSpecialisationId = ref(null);
const slideIndex = ref(0);

async function loadServices() {
  loading.value = true;
  try {
    const response = await fetch('/api/services/carousel', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const payload = await response.json();
    services.value = Array.isArray(payload.services) ? payload.services : [];
  } catch {
    services.value = [];
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  if (typeof window !== 'undefined') {
    mobileMediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    syncMobileViewport = () => {
      isMobileViewport.value = mobileMediaQuery.matches;
    };
    syncMobileViewport();
    mobileMediaQuery.addEventListener('change', syncMobileViewport);
  }

  loadServices();
});

onBeforeUnmount(() => {
  if (mobileMediaQuery && syncMobileViewport) {
    mobileMediaQuery.removeEventListener('change', syncMobileViewport);
  }
});

// Filter badges (specialisations) derived from the loaded services, sorted
// alphabetically like the Figma design.
const specialisations = computed(() => {
  const map = new Map();
  services.value.forEach((service) => {
    (service.specialisations || []).forEach((spec) => {
      if (!map.has(spec.id)) map.set(spec.id, spec);
    });
  });
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, 'pl'));
});

const filteredServices = computed(() => {
  if (selectedSpecialisationId.value == null) return services.value;
  return services.value.filter((service) => (
    (service.specialisations || []).some((spec) => spec.id === selectedSpecialisationId.value)
  ));
});

const pages = computed(() => {
  const out = [];
  for (let i = 0; i < filteredServices.value.length; i += PER_PAGE) {
    out.push(filteredServices.value.slice(i, i + PER_PAGE));
  }
  return out;
});

const maxSlideIndex = computed(() => Math.max(0, pages.value.length - 1));
const dotCount = computed(() => pages.value.length);

const canGoPrev = computed(() => slideIndex.value > 0);
const canGoNext = computed(() => slideIndex.value < maxSlideIndex.value);

const {
  viewportRef,
  viewportClass,
  trackClass,
  trackTransitionClass,
  swipeHandlers,
} = useCarouselSwipe({
  onNext: () => next(),
  onPrev: () => prev(),
  enabled: () => !loading.value && pages.value.length > 1,
  canGoNext,
  canGoPrev,
});

// Gap between slide pages — tighter on mobile, still clears card shadows on desktop.
const pageGap = computed(() => (isMobileViewport.value ? '1.5rem' : '2.5rem'));

const trackStyle = computed(() => ({
  gap: pageGap.value,
  ...carouselTrackTransform(`-${slideIndex.value} * (var(--card-width) + ${pageGap.value})`),
}));

watch(selectedSpecialisationId, () => {
  slideIndex.value = 0;
});

watch(maxSlideIndex, (max) => {
  if (slideIndex.value > max) slideIndex.value = max;
});

function next() {
  if (!canGoNext.value) return;
  slideIndex.value += 1;
}

function prev() {
  if (!canGoPrev.value) return;
  slideIndex.value -= 1;
}

function goToSlide(index) {
  slideIndex.value = Math.min(Math.max(0, index), maxSlideIndex.value);
}

function serviceHref(service) {
  return serviceRoute(service.slug);
}

const examinationsHref = computed(() => `${servicesIndexRoute()}?category=badanie`);
const proceduresHref = computed(() => `${servicesIndexRoute()}?category=zabieg`);
</script>

<style scoped>
.services-carousel-viewport {
  --card-width: 100%;
}
</style>
